/**
 * Single source of truth for the "Ostetaan autoja" SEO landing pages. Route
 * table, prerender list, sitemap, footer links and e2e specs all derive from
 * these lists, so adding a city or maker is a one-line change here.
 *
 * Must NOT import from files that use `@/` aliases or browser code, because
 * `react-router.config.ts` and `app/routes.ts` load it in plain Node.
 */

export interface CityNameProps {
  basic: string;
  inessive: string;
  genetive: string;
  elative: string;
}

export interface CityLandingPage {
  slug: string;
  cityName: CityNameProps;
}

export interface MakerLandingPage {
  slug: string;
  carMaker: string;
}

const WE_BUY_CARS_PATH = "/ostetaan-autoja";

const CITY_LANDING_PAGES: readonly CityLandingPage[] = [
  {
    slug: "helsinki",
    cityName: {
      basic: "Helsinki",
      inessive: "Helsingissä",
      genetive: "Helsingin",
      elative: "Helsingiltä"
    }
  },
  {
    slug: "espoo",
    cityName: {
      basic: "Espoo",
      inessive: "Espoossa",
      genetive: "Espoon",
      elative: "Espoosta"
    }
  },
  {
    slug: "vantaa",
    cityName: {
      basic: "Vantaa",
      inessive: "Vantaalla",
      genetive: "Vantaan",
      elative: "Vantaalta"
    }
  },
  {
    slug: "turku",
    cityName: {
      basic: "Turku",
      inessive: "Turussa",
      genetive: "Turun",
      elative: "Turulta"
    }
  },
  {
    slug: "tampere",
    cityName: {
      basic: "Tampere",
      inessive: "Tampereella",
      genetive: "Tampereen",
      elative: "Tampereelta"
    }
  },
  {
    slug: "oulu",
    cityName: {
      basic: "Oulu",
      inessive: "Oulussa",
      genetive: "Oulun",
      elative: "Oulusta"
    }
  }
];

const MAKER_LANDING_PAGES: readonly MakerLandingPage[] = [
  { slug: "audi", carMaker: "Audi" },
  { slug: "bmw", carMaker: "BMW" },
  { slug: "ford", carMaker: "Ford" },
  { slug: "mercedes-benz", carMaker: "Mercedes-Benz" },
  { slug: "nissan", carMaker: "Nissan" },
  { slug: "opel", carMaker: "Opel" },
  { slug: "skoda", carMaker: "Skoda" },
  { slug: "toyota", carMaker: "Toyota" },
  { slug: "volkswagen", carMaker: "Volkswagen" },
  { slug: "volvo", carMaker: "Volvo" },
  { slug: "tesla", carMaker: "Tesla" }
];

const toLandingPath = (slug: string): string => `${WE_BUY_CARS_PATH}-${slug}`;

// Route ids match the path without the leading slash, e.g.
// "ostetaan-autoja-helsinki".
const toLandingRouteId = (slug: string): string => toLandingPath(slug).slice(1);

const CITY_LANDING_PATHS = CITY_LANDING_PAGES.map(({ slug }) =>
  toLandingPath(slug)
);

const MAKER_LANDING_PATHS = MAKER_LANDING_PAGES.map(({ slug }) =>
  toLandingPath(slug)
);

const WE_BUY_CARS_PATHS: readonly string[] = [
  WE_BUY_CARS_PATH,
  ...CITY_LANDING_PATHS,
  ...MAKER_LANDING_PATHS
];

// Prerender requests paths with a trailing slash ("/ostetaan-autoja-oulu/").
const normalizePathname = (pathname: string): string =>
  pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;

const findCityByPath = (pathname: string): CityNameProps | undefined => {
  const path = normalizePathname(pathname);
  return CITY_LANDING_PAGES.find(({ slug }) => toLandingPath(slug) === path)
    ?.cityName;
};

const findMakerByPath = (pathname: string): string | undefined => {
  const path = normalizePathname(pathname);
  return MAKER_LANDING_PAGES.find(({ slug }) => toLandingPath(slug) === path)
    ?.carMaker;
};

export {
  CITY_LANDING_PAGES,
  CITY_LANDING_PATHS,
  MAKER_LANDING_PAGES,
  MAKER_LANDING_PATHS,
  WE_BUY_CARS_PATH,
  WE_BUY_CARS_PATHS,
  findCityByPath,
  findMakerByPath,
  normalizePathname,
  toLandingPath,
  toLandingRouteId
};
